import { Component } from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import { Router } from '@angular/router'

import { environment } from '../../../../../../environments/environment'
import { flashMessages } from '../../../../../../locale/flash-messages/flash-messages.en'
import { metaDataContent } from '../../../../../../locale/meta-data/meta-data.content.en'
import { appConstants } from '../../../../../app.constants'
import { ResourceSlug } from '../../../../../shared/enums/wordpress/wp-resource-slug.enum'
import { BreadcrumbLink } from '../../../../../shared/interfaces/breadcrumb-link.interface'
import { Post } from '../../../../../shared/interfaces/resources/post.interface'
import { SelectOption } from '../../../../../shared/interfaces/select-option.interface'
import { BreadcrumbService } from '../../../../../shared/services/breadcrumb.service'
import { CatalogueService } from '../../../../../shared/services/catalogue.service'
import { FlashMessageService } from '../../../../../shared/services/flash-message.service'
import { MetaService } from '../../../../../shared/services/meta.service'
import { PostService } from '../../../../../shared/services/post.service'
import { Duration } from '../../../enums/duration.enum'
import { RelationWithTool } from '../../../enums/relation-with-tool.enum'
import { WordCountValidators } from '../../../partials/rich-text-input/word-count.validator'
import { ToolService } from '../../../services/tool.service'
import { ToolDto } from '../dtos/tool.dto'

@Component({
  selector: 'app-tool-create',
  templateUrl: './tool-create.component.html',
  styleUrls: ['./tool-create.component.scss']
})
export class ToolCreateComponent {
  form: FormGroup

  createTaxonomyFunction: (
    slug: string,
    name: string
  ) => Promise<SelectOption[]> = (slug: string, name: string) =>
    this.catalogueService.store(slug, { name })

  taxonomyGroups: {
    name: string
    description?: string
    taxonomies: {
      label: string
      placeholder: string
      slug: ResourceSlug
      propName: string
      maxSelectedItems?: number
      options?: SelectOption[]
      createFunction?: (name: string) => Promise<SelectOption[]>
    }[]
  }[] = [
    {
      name: 'Type',
      taxonomies: [
        {
          label: 'Approaches',
          propName: 'approachIds',
          placeholder: `Select the tool's approach (i.e. technical, procedural, educational or other)...`,
          slug: ResourceSlug.Approaches
        },
        {
          label: 'Tool types (max. 3)',
          propName: 'toolTypeIds',
          placeholder:
            'Select  the type of tool (i.e. toolkit, software tool, technical documentation, etc.)... ',
          slug: ResourceSlug.ToolTypes,
          maxSelectedItems: 3
        }
      ]
    },
    {
      name: 'Usage rights & objectives',
      taxonomies: [
        {
          label: 'Usage rights (max. 3)',
          propName: 'usageRightIds',
          placeholder: 'Select the conditions for using the tool...',
          slug: ResourceSlug.UsageRights,
          maxSelectedItems: 3
        },
        {
          label: 'Objectives (max. 3)',
          propName: 'objectiveIds',
          placeholder: 'Help achieve the following objective(s)...',
          slug: ResourceSlug.Objectives,
          maxSelectedItems: 3
        },
        {
          label: 'License',
          propName: 'licenseIds',
          placeholder:
            'Select or specify any licensing requirements for the use of this tool...',
          slug: ResourceSlug.Licenses,
          createFunction: (newTermName: string) =>
            this.createTaxonomyFunction(
              `taxonomies/${ResourceSlug.Licenses}`,
              newTermName
            )
        }
      ]
    },
    {
      name: 'Origin',
      taxonomies: [
        {
          label: 'Stakeholder groups (max. 3)',
          propName: 'stakeholderGroupIds',
          placeholder:
            'Select the type of stakeholder group(s) that developed the tool...',
          slug: ResourceSlug.StakeholderGroups,
          maxSelectedItems: 3
        },
        {
          label: 'Country of origin',
          propName: 'countryIds',
          placeholder: 'Select the country from which the tool originates...',
          slug: ResourceSlug.Countries
        },
        {
          label: 'Organisation',
          propName: 'organisationIds',
          placeholder: 'The organisation that developed the tool...',
          slug: ResourceSlug.Organisations,
          createFunction: (newTermName: string) =>
            this.createTaxonomyFunction(
              `taxonomies/${ResourceSlug.Organisations}`,
              newTermName
            )
        }
      ]
    },
    {
      name: 'Scope',
      taxonomies: [
        {
          label: 'Lifecycle stages (max. 3)',
          propName: 'lifecycleIds',
          placeholder:
            'Select the stage(s) of the AI system lifecycle that the tool helps to implement...',
          slug: ResourceSlug.Lifecycles,
          maxSelectedItems: 3
        },
        {
          label: 'Target Groups (max. 3)',
          propName: 'targetGroupIds',
          placeholder:
            'Select the stakeholder group(s) expected to use the tool...',
          slug: ResourceSlug.TargetGroups,
          maxSelectedItems: 3
        },
        {
          label: 'Target users (max. 3)',
          propName: 'targetUserIds',
          placeholder: 'Select the target users...',
          slug: ResourceSlug.TargetUsers,
          maxSelectedItems: 3
        },
        {
          label: 'Target Sectors (max. 3)',
          propName: 'targetSectorIds',
          placeholder:
            'Select the target sector(s) where the tool is expected to be implemented...',
          slug: ResourceSlug.TargetSectors,
          maxSelectedItems: 3
        },

        {
          label: 'Impacted stakeholders (max. 3)',
          propName: 'impactedStakeholderIds',
          placeholder:
            'Select the stakeholders likely impacted by the use of this tool...',
          slug: ResourceSlug.ImpactedStakeholder,
          maxSelectedItems: 3
        },
        {
          label: 'Purposes or action of the AI system (max. 3)',
          propName: 'purposeIds',
          placeholder: 'Select the purpose or action of the AI system...',
          slug: ResourceSlug.Purposes,
          maxSelectedItems: 3,
          createFunction: (newTermName: string) =>
            this.createTaxonomyFunction(
              `taxonomies/${ResourceSlug.Purposes}`,
              newTermName
            )
        },
        {
          label: 'Geographical reach',
          propName: 'geographicalScopeIds',
          placeholder:
            'Select the geographical reach of the tool (by country or region), if applicable...',
          slug: ResourceSlug.GeographicalScope
        },
        {
          label: 'Platform neutrality',
          propName: 'technologyPlatformIds',
          placeholder:
            'Select whether the tool is technology platform-neutral or platform-specific...',
          slug: ResourceSlug.TechnologyPlatform
        }
      ]
    },
    {
      name: 'Adoptability',
      taxonomies: [
        {
          label: 'Tool readiness',
          propName: 'maturityIds',
          placeholder: 'Select the project phase the tool is currently in...',
          slug: ResourceSlug.Maturity
        },

        {
          label: 'Required Skills',
          propName: 'requiredSkillIds',
          placeholder:
            'Select the groups that will be impacted by the use of the tool...',
          slug: ResourceSlug.RequiredSkills
        },
        {
          label: 'People Involved (max. 3)',
          propName: 'peopleInvolvedIds',
          placeholder:
            'Stakeholders who should be involved in the implementation and operation of the tool...',
          slug: ResourceSlug.PeopleInvolved,
          maxSelectedItems: 3
        },
        {
          label: 'Validity',
          propName: 'validityIds',
          placeholder:
            'Select the degree to which the tool is kept up to date...',
          slug: ResourceSlug.Validities
        }
      ]
    },
    {
      name: 'Implementation incentives',
      taxonomies: [
        {
          label: 'Benefits (max. 3)',
          propName: 'benefitIds',
          placeholder: 'Select the expected benefits of using the tool...',
          slug: ResourceSlug.Benefit,
          maxSelectedItems: 3
        },
        {
          label: 'Enforcements',
          propName: 'enforcementIds',
          placeholder:
            'Select the enforcement mechanisms attached with the usage of this tool...',
          slug: ResourceSlug.Enforcement
        }
      ]
    },
    {
      name: 'Keywords',
      taxonomies: [
        {
          label: 'Keywords',
          propName: 'tagIds',
          placeholder: 'Add keywords to the tool...',
          slug: ResourceSlug.Tags,
          createFunction: (newTermName: string) =>
            this.createTaxonomyFunction(
              `taxonomies/${ResourceSlug.Tags}`,
              newTermName
            )
        }
      ]
    }
  ]

  showErrors = false
  invalidInputs: string[]
  isRelatedPostInvalid = false
  showImageGalleryModal: boolean

  wpToolId: number
  RelationWithTool = RelationWithTool
  Duration = Duration
  captchaKey = appConstants.RECAPTCHA_SITE_KEY
  storagePath: string = environment.apiCatalogStoragePath

  loadingSubmit = false
  educationalApproachId: number

  breadcrumbLinks: BreadcrumbLink[]

  constructor(
    private formBuilder: FormBuilder,
    private toolService: ToolService,
    private flashMessageService: FlashMessageService,
    private catalogueService: CatalogueService,
    private postService: PostService,
    private router: Router,
    breadcrumbService: BreadcrumbService,
    metaService: MetaService
  ) {
    this.breadcrumbLinks = breadcrumbService.getBreadCrumbLinks('toolCatalog', [
      {
        label: 'Tools',
        path: '/catalogue/tools'
      },
      {
        label: 'Submit a tool'
      }
    ])
    metaService.setTags(metaDataContent.toolCatalogToolSubmit)
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      title: ['', [Validators.required]],
      excerpt: ['', [Validators.required, WordCountValidators.max(80)]],
      description: ['', [Validators.required, WordCountValidators.max(500)]],
      gitlabLink: '',
      slackLink: '',
      toolPackage: '',
      websiteLink: '',
      githubLink: '',
      huggingFaceLink: '',
      relatedPostId: null,
      image: '',
      defaultImage: '',
      recaptchaReactive: [null, [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      relationWithTool: [null, Validators.required],
      duration: null,
      consent: [false, [Validators.requiredTrue]],
      consentPublicationDelay: [false, [Validators.requiredTrue]]
    })

    this.form.setValidators([
      this.atLeastOneLinkIsFilled([
        'gitlabLink',
        'slackLink',
        'toolPackage',
        'websiteLink',
        'githubLink',
        'huggingFaceLink'
      ])
    ])

    this.taxonomyGroups.forEach((taxonomyGroup, groupIndex) => {
      taxonomyGroup.taxonomies.forEach((taxonomy, taxIndex) => {
        // Add dynamic form control.
        this.form.addControl(taxonomy.propName, new FormControl([]))

        this.catalogueService
          .listSelectOptions(taxonomy.slug)
          .then((filterTaxonomyRes: SelectOption[]) => {
            taxonomy.options = filterTaxonomyRes

            if (groupIndex === 0 && taxIndex === 0) {
              // Get the educational approach taxonomy id for conditional display.
              this.educationalApproachId = taxonomy.options.find(
                (option) => option.label === 'Educational'
              )?.value as number
            }
          })
      })
    })
  }

  onTaxonomySelected(event: (number | string)[], propName: string) {
    this.form.get(propName).setValue(event)

    // Reset duration field if not educational approach.
    if (
      !this.form.get('approachIds').value.includes(this.educationalApproachId)
    ) {
      this.form.get('duration').setValue(null)
    }
  }

  onFieldChanged(event: number | string, formControlName: string) {
    this.form.get(formControlName).setValue(event)
  }

  submit() {
    if (this.form.invalid) {
      this.invalidInputs = this.findInvalidControls()

      if (this.form.errors?.atLeastOneLinkIsFilled) {
        this.invalidInputs.push('links')
      }
      this.showErrors = true
      return this.flashMessageService.error(
        'Please fill in all required fields and click on the CAPTCHA to submit the tool.'
      )
    }

    this.loadingSubmit = true

    const toolDto: ToolDto = {
      name: this.form.value.title,
      content: this.form.value.description,
      excerpt: this.form.value.excerpt,
      websiteLink: this.form.value.websiteLink,
      githubLink: this.form.value.githubLink,
      gitLabLink: this.form.value.gitlabLink,
      huggingFaceLink: this.form.value.huggingFaceLink,
      slackLink: this.form.value.slackLink,
      toolPackage: this.form.value.toolPackage,
      relatedPostId: this.form.value.relatedPostId,
      email: this.form.value.email,
      relationWithTool: this.form.value.relationWithTool,
      duration: this.form.value.duration,
      image: this.form.value.image || this.form.value.defaultImage
    }

    // Apply dynamic taxonomy input values to toolDto.
    this.taxonomyGroups.forEach((taxonomyGroup) => {
      taxonomyGroup.taxonomies.forEach((taxonomy) => {
        if (this.form.value[taxonomy.propName]) {
          toolDto[taxonomy.propName] = this.form.value[taxonomy.propName]
        }
      })
    })

    this.toolService.store(toolDto).then(
      async () => {
        this.router.navigate(['/catalogue/tools/submit/success'])
        this.loadingSubmit = false
      },
      () => {
        this.flashMessageService.error(flashMessages.tool.error.cannotSend)
        this.loadingSubmit = false
      }
    )
  }

  private findInvalidControls(): string[] {
    const invalid = []
    const controls = this.form.controls
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name)
      }
    }
    return invalid
  }

  public atLeastOneLinkIsFilled(controlNames: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const atLeastOneLinkIsFilled = controlNames.some(
        (controlName) => control.get(controlName).value !== ''
      )

      if (!atLeastOneLinkIsFilled) {
        return { atLeastOneLinkIsFilled: true }
      }
      return null
    }
  }

  onDescriptionChanged(wysiwygInputValue: string) {
    this.form.get('description').setValue(wysiwygInputValue)
  }

  onRelatedPostInputChange(postUrl: string) {
    this.isRelatedPostInvalid = false
    if (postUrl === '') {
      this.form.get('relatedPostId').setValue(null)
      return
    }

    // Verify that the user URL is within the OECD.AI website.
    const postLanguage: {
      code: string
      name: string
      url: string
    } = environment.availableLanguages.find((lang) =>
      postUrl.includes(`${lang.code}/wonk/`)
    )

    if (!postLanguage) {
      this.isRelatedPostInvalid = true
      return this.flashMessageService.error(
        'Your url does not belong the OECD.AI Wonk.'
      )
    }

    this.postService
      .show({
        slug: postUrl.replace(`${postLanguage.url}/wonk/`, ''),
        lang: postLanguage.code
      })
      .then((postRes: Post) => {
        this.form.get('relatedPostId').setValue(postRes.id)
      })
      .catch((err) => {
        this.isRelatedPostInvalid = true
        this.form.get('relatedPostId').setValue(null)
        this.flashMessageService.error(err)
      })
  }
}
